import i18n from "i18next";
import { type FC, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";

import { useEmvConfigurationDeleteMutation } from "src/app/features/EmvKernelProfile/subFeatures/EmvConfiguration/services/server/EmvConfigurationApi";
import type { EmvConfigurationEntity } from "src/app/features/EmvKernelProfile/subFeatures/EmvConfiguration/types/EmvConfigurationEntity";
import type { ExtendedColumnsType } from "../../../../../../../components";
import type { AntCustomFormField, CommonFieldAttributes } from "../../../../../../../components/Form/types/types";
import { PropertyTable } from "../../../../../../../components/Table/components/PropertyTable";
import { Actions } from "../../../../../../../components/Table/types/types";
import { ResourceName, ResourceVariant } from "../../../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../../../core/router/types/routerTypes";
import {
    showSuccessNotification,
    showWarningNotification,
} from "../../../../../../../core/setup/redux/state/notifications/notificationsState";

const getColumnsConfig = (t: Function): ExtendedColumnsType<EmvConfigurationEntity>[] => {
    return [
        {
            title: t("emvKernelConfiguration.id"),
            key: "id",
            dataIndex: "id",
        },
        {
            title: t("emvKernelConfigurationCreateForm.type"),
            key: "type",
            dataIndex: "type",
        },
        {
            title: t("emvKernelConfiguration.name"),
            key: "name",
            dataIndex: "name",
        },
        {
            title: t("emvKernelConfigurationCreateForm.aid"),
            key: "aid",
            dataIndex: "aid",
        },
        {
            title: t("emvKernelConfigurationCreateForm.appVersionPrimary"),
            key: "appVersionPrimary",
            dataIndex: "appVersionPrimary",
        },
        {
            title: t("emvKernelConfigurationCreateForm.appSelectionIndicatorAidShort"),
            dataIndex: "appSelectionIndicator",
            key: "appSelectionIndicator",
        },
    ];
};

export interface EmvConfigurationTableProps extends AntCustomFormField<EmvConfigurationEntity[]>, CommonFieldAttributes {}

export const EmvConfigurationsTable: FC<EmvConfigurationTableProps> = ({ value = [], onChange, readOnly }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const resourceUrl = `${ResourceName.EMV_KERNEL_PROFILES}/${ResourceVariant.APAC}/${id}/${ResourceName.EMV_CONFIGURATIONS}`;

    const onDataChanged = (data: EmvConfigurationEntity[]) => {
        if (onChange) {
            onChange(data);
        }
    };

    const onRowClick = (record: EmvConfigurationEntity) => navigate(`${BASE_PATH}/${resourceUrl}/${FORM_ACTION.EDIT}/${record.id}`);

    const addCallback = () => {
        navigate(`${BASE_PATH}/${resourceUrl}/${FORM_ACTION.CREATE}`);
    };

    const [deleteTableItem] = useEmvConfigurationDeleteMutation();

    const deleteCallback = (item: EmvConfigurationEntity) => {
        deleteTableItem({
            emvKernelProfileId: id,
            id: item.id,
        })
            .unwrap()
            .then((payload) => performDeleteSuccess(payload))
            .catch(() => {
                dispatch(
                    showWarningNotification({
                        text: i18n.t("common.formDeleteError", {
                            id: item.id,
                        }),
                    }),
                );
            });

        const performDeleteSuccess = (payload: EmvConfigurationEntity) => {
            if (payload) {
                notifyOkDelete(payload);
            }
            onDataChanged(value.filter((record) => record.id !== item.id));
        };
    };
    const notifyOkDelete = useCallback(
        (payload: EmvConfigurationEntity) => {
            dispatch(
                showSuccessNotification({
                    text: i18n.t("common.formDeleteSuccess", {
                        id: payload.id,
                        resource: payload.name,
                    }),
                    autoCloseDisabled: false,
                }),
            );
        },
        [dispatch],
    );

    return (
        <PropertyTable<EmvConfigurationEntity>
            getColumnsConfig={getColumnsConfig}
            name="emvConfiguration_table"
            propertyAsRowKey="id"
            tableData={value || []}
            readOnly={readOnly || false}
            onDataChanged={onDataChanged}
            onRow={(record: EmvConfigurationEntity) => {
                return {
                    onClick: () => onRowClick(record),
                };
            }}
            resourceUrl={resourceUrl}
            actions={[Actions.DELETE, Actions.ADD, Actions.COPY]}
            actionCallbacks={{ add: addCallback, delete: (item?: EmvConfigurationEntity) => item && deleteCallback(item) }}
            addButtonName="emvConfigurationsTable_addButton"
        />
    );
};
